import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Copy, Check } from "lucide-react";
import InsuranceBadge from "@/components/landing/InsuranceBadge";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const SERVICES = ["Interior Painting", "Exterior Painting", "Cabinet Refinishing", "Power Washing & Prep", "Full Home"];

export default function QuoteForm({ content }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_type: "Interior Painting", property_type: "Residential", message: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const tel = `tel:${(content.phone || "").replace(/\D/g, "")}`;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success("Request received! We'll call you back shortly.");
      setForm({ name: "", phone: "", email: "", service_type: "Interior Painting", property_type: "Residential", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(content.phone);
      setCopied(true);
      toast.success("Phone number copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const inputCls = "bg-ink/60 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-royal h-12 rounded-xl";

  return (
    <section id="quote" className="relative py-24 sm:py-32 overflow-hidden" data-testid="quote-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_20%_40%,rgba(37,99,235,0.14),transparent)]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }}>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-sky mb-4">Free Written Estimate</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">{content.cta_heading}</h2>
          <p className="mt-5 text-slate-400 leading-relaxed max-w-md">{content.cta_sub}</p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <a href={tel} data-testid="quote-phone-link"
                className="inline-flex items-center gap-3 rounded-2xl bg-royal px-6 py-4 font-display font-extrabold text-xl hover:bg-royalhover transition-colors duration-200">
                <Phone className="h-5 w-5" /> {content.phone}
              </a>
              <button onClick={copyPhone} data-testid="quote-copy-phone" aria-label="Copy phone number"
                className="p-3 rounded-xl border border-white/15 text-slate-400 hover:text-sky hover:border-sky/50 transition-colors duration-200">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <p className="flex items-center gap-3 text-sm text-slate-400"><MapPin className="h-4 w-4 text-sky shrink-0" /> {content.service_area}</p>
            <p className="flex items-center gap-3 text-sm text-slate-400"><Clock className="h-4 w-4 text-sky shrink-0" /> Fast callbacks — usually same day</p>
            <InsuranceBadge text={content.insurance_badge} src={content.certificate_url} className="text-sm text-slate-400" />
          </div>
        </motion.div>

        <motion.form onSubmit={submit}
          initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, delay: 0.15 }}
          className="rounded-3xl border border-white/10 bg-card/50 backdrop-blur p-7 sm:p-9 space-y-4"
          data-testid="quote-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input value={form.name} onChange={set("name")} placeholder="Full name *" required data-testid="quote-form-name-input" className={inputCls} />
            <Input value={form.phone} onChange={set("phone")} placeholder="Phone *" required type="tel" data-testid="quote-form-phone-input" className={inputCls} />
          </div>
          <Input value={form.email} onChange={set("email")} placeholder="Email (optional)" type="email" data-testid="quote-form-email-input" className={inputCls} />
          <div className="grid sm:grid-cols-2 gap-4">
            <select value={form.service_type} onChange={set("service_type")} data-testid="quote-form-service-select"
              className={`${inputCls} w-full px-3 text-sm border rounded-xl`}>
              {SERVICES.map((s) => <option key={s} value={s} className="bg-navy">{s}</option>)}
            </select>
            <select value={form.property_type} onChange={set("property_type")} data-testid="quote-form-property-select"
              className={`${inputCls} w-full px-3 text-sm border rounded-xl`}>
              {["Residential", "Commercial"].map((p) => <option key={p} value={p} className="bg-navy">{p}</option>)}
            </select>
          </div>
          <Textarea value={form.message} onChange={set("message")} placeholder="Tell us about your project (rooms, colors, timeline...)" rows={4}
            data-testid="quote-form-message-input" className="bg-ink/60 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-royal rounded-xl" />
          <button type="submit" disabled={sending} data-testid="quote-form-submit-button"
            className="w-full rounded-xl bg-royal py-4 font-display font-bold text-base hover:bg-royalhover transition-all duration-300 hover:shadow-[0_0_36px_rgba(37,99,235,0.4)] disabled:opacity-60">
            {sending ? "Sending..." : "Request My Free Estimate"}
          </button>
          <p className="text-center text-xs text-slate-500">{content.tagline}</p>
        </motion.form>
      </div>
    </section>
  );
}
